import type { GetServerSideProps } from 'next'
import { fetchCelebrities } from '@/lib/serverApi'
import { absUrl, breadcrumbLd, plainText, SeoProps } from '@/utils/seo'
import IconFont from '@/components/IconFonts'
import useCelebrities from '@/hooks/swr/useCelebrities'
import { Celebrity } from '@/utils/types'
import Link from 'next/link'
import { useMemo, useState } from 'react'

// 分类展示:普通词条按 description(分类)分组;编委/选手归入「名人堂」分类。
// 已知术语分类固定排序在前,其余分类居中,名人堂殿后。所有分类默认展开。配色沿用全站清爽体系。
const CATEGORY_ORDER = ['复式/移位术语', '瑞士制/编排术语', '掼蛋计分术语', '通用赛制']
const PEOPLE_CATEGORY = '名人堂'
const CATEGORY_EMOJI: Record<string, string> = {
  '复式/移位术语': '🎯',
  '瑞士制/编排术语': '🔀',
  '掼蛋计分术语': '🧮',
  通用赛制: '🏆',
  名人堂: '👑',
  其他: '📄',
}

const Celebrities = () => {
  const { data: celebrities } = useCelebrities()
  const [keywords, setKeywords] = useState('')
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})

  const filtered = useMemo(() => (celebrities ?? []).filter((c) => JSON.stringify(c)?.includes(keywords)), [celebrities, keywords])

  const groups = useMemo(() => {
    const map: Record<string, Celebrity[]> = {}
    const push = (key: string, b: Celebrity) => {
      map[key] = map[key] || []
      map[key].push(b)
    }
    filtered.forEach((b) => {
      // 名人堂 = 编委 / 选手;其它(is_other 或无标记)按分类归入术语/其他,不进名人堂
      const isPeople = b.role?.is_editor || b.role?.is_player
      if (isPeople) push(PEOPLE_CATEGORY, b)
      else if (b.details) push(b.description || '其他', b)
    })
    const known = CATEGORY_ORDER.filter((k) => map[k])
    const others = Object.keys(map)
      .filter((k) => !CATEGORY_ORDER.includes(k) && k !== PEOPLE_CATEGORY)
      .sort()
    const tail = map[PEOPLE_CATEGORY] ? [PEOPLE_CATEGORY] : []
    return [...known, ...others, ...tail].map((k) => ({ key: k, items: map[k] }))
  }, [filtered])

  const searching = keywords.trim().length > 0

  return (
    <>
      <div className="min-h-screen bg-gray-50">
        <div className="mx-auto max-w-4xl px-4 py-10 md:py-14">
          <header className="mb-8 text-center">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-xs font-medium text-gray-600 shadow-sm ring-1 ring-gray-200">
              📚 术语 · 名人一处速查
            </span>
            <h1 className="mt-4 text-4xl font-black tracking-tight text-gray-900 md:text-5xl">灵缇百科</h1>
            <p className="mt-3 text-gray-500">赛制术语、计分口径与名人堂，按分类速查、可搜索。</p>
          </header>

          <input
            value={keywords}
            onChange={(e) => setKeywords(e.target.value)}
            placeholder="搜索术语 / 名人，如 Matchpoint、级差分…"
            className="mb-8 box-border w-full appearance-none rounded-xl border border-solid border-gray-200 bg-white px-4 py-2.5 text-base leading-normal text-gray-900 shadow-sm outline-none focus:border-gray-900"
          />

          {groups.map((g) => {
            const open = searching || !collapsed[g.key]
            return (
              <section key={g.key} className="mb-8">
                <div
                  onClick={() => setCollapsed((s) => ({ ...s, [g.key]: !s[g.key] }))}
                  className="mb-3 flex cursor-pointer select-none items-center gap-2 border-b border-gray-100 pb-2 text-lg font-bold text-gray-800"
                >
                  <span>{CATEGORY_EMOJI[g.key] || '📄'}</span>
                  {g.key}
                  <span className="text-sm font-normal text-gray-400">{g.items.length}</span>
                  <IconFont icon="icon-play" className={`ml-auto text-sm text-gray-300 transition-transform ${open ? 'rotate-90' : ''}`} />
                </div>
                {open && (
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                    {g.items.map((b) => (
                      <Link href={`/baike/${b.short_name}`} className="no-underline" key={b.id}>
                        <div className="flex w-full items-center justify-between rounded-xl bg-white p-4 text-gray-900 shadow-sm ring-1 ring-black/5 transition hover:-translate-y-0.5 hover:shadow-md">
                          <span className="truncate">{b.name}</span>
                          <IconFont icon="icon-arrow-right-bold" className="ml-2 shrink-0 text-gray-300" />
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </section>
            )
          })}
        </div>
      </div>
    </>
  )
}

// 服务端取全部词条:术语定义直出,AI/搜索引擎可以直接引用;同时作为 SWR fallback
export const getServerSideProps: GetServerSideProps = async ({ res }) => {
  const list = await fetchCelebrities()
  res.setHeader('Cache-Control', 'public, s-maxage=600, stale-while-revalidate=86400')
  const terms = list.filter((b) => b.details && !(b.role?.is_editor || b.role?.is_player))
  const seo: SeoProps = {
    title: '灵缇百科 · 掼蛋/复式赛/瑞士制赛事术语与计分口径速查',
    description: `灵缇百科收录 ${list.length} 个赛事词条:瑞士制编排、复式赛移位(米切尔/豪威尔)、Matchpoint、级差分、场分、小分、对手分、轮空等术语定义,以及掼蛋名人堂。`,
    keywords: '掼蛋术语,复式赛,瑞士制,米切尔移位,豪威尔移位,Matchpoint,级差分,场分,小分,对手分,布赫兹,轮空,种子,灵缇百科',
    path: '/baike',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'DefinedTermSet',
        '@id': absUrl('/baike#terms'),
        name: '灵缇百科 · 赛事术语',
        url: absUrl('/baike'),
        inLanguage: 'zh-CN',
        hasDefinedTerm: terms.map((b) => ({
          '@type': 'DefinedTerm',
          name: b.name,
          termCode: b.short_name,
          description: plainText(b.details, 200),
          url: absUrl(`/baike/${b.short_name}`),
        })),
      },
      breadcrumbLd([
        ['首页', '/'],
        ['灵缇百科', '/baike'],
      ]),
    ],
  }
  return { props: { seo, fallback: { celebrities: list } } }
}

export default Celebrities
